import { useState } from 'preact/hooks'
import type { JSX } from 'preact'

/**
 * Drag-to-reorder state and handlers for a list rendered as rows.
 *
 * The same two pieces of state and the same five handlers were hand-rolled in AgendaSection,
 * SpeakerListSection and BenefitsCard — identical logic and identical drag-over styling in all
 * three, written out inline on the row element. That inlining is most of why those rows are hard to
 * read; AgendaSection's was a single ~400-character line.
 *
 * `move` is the reorder itself, and is supplied by the caller — react-hook-form's `useFieldArray`
 * gives one directly, and a plain array splice works just as well.
 *
 * Keyboard reordering is deliberately not here. It is an accessibility affordance on a specific
 * grip control rather than part of the drag concern, and only one of the three lists offers it.
 *
 * @example
 * const { isDragOver, dragProps } = useDragReorder(move)
 * items.map((item, idx) => <div key={item.id} {...dragProps(idx)} className={isDragOver(idx) ? … : …} />)
 */
export function useDragReorder(move: (from: number, to: number) => void) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  /** Clears both ends of the gesture. Called on drop and on a cancelled drag alike. */
  const reset = () => {
    setDragIndex(null)
    setDragOverIndex(null)
  }

  /**
   * True only for a row being hovered by a drag that started somewhere else — a row dragged over
   * itself is not a drop target and must not light up.
   */
  const isDragOver = (idx: number) => dragOverIndex === idx && dragIndex !== null && dragIndex !== idx

  /** Spread onto the row element. */
  const dragProps = (idx: number): JSX.HTMLAttributes<HTMLDivElement> => ({
    draggable: true,
    onDragStart: (e: JSX.TargetedDragEvent<HTMLDivElement>) => {
      setDragIndex(idx)
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        // Firefox refuses to start a drag at all unless dragstart sets some payload, so
        // without this the whole gesture is dead there — no drag image, no dragover, no
        // drop. The reorder itself reads dragIndex from state and ignores this value.
        // Every in-list drop calls preventDefault below, which cancels the text insertion
        // this would otherwise cause when a row is released over one of its own inputs.
        e.dataTransfer.setData('text/plain', String(idx))
      }
    },
    onDragOver: (e: JSX.TargetedDragEvent<HTMLDivElement>) => {
      // Without preventDefault the browser refuses the drop outright.
      e.preventDefault()
      if (dragOverIndex !== idx) setDragOverIndex(idx)
    },
    onDragLeave: () => {
      // Guarded: leaving row A can arrive after entering row B, and an unguarded clear would
      // wipe B's highlight mid-drag.
      if (dragOverIndex === idx) setDragOverIndex(null)
    },
    onDrop: (e: JSX.TargetedDragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (dragIndex !== null && dragIndex !== idx) move(dragIndex, idx)
      reset()
    },
    onDragEnd: reset,
  })

  return { isDragOver, dragProps, reset }
}
